import { useEffect, useMemo, useRef, useState } from 'react'
import { AtSign, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { Button } from '@/components/ui/button'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { highlightBrand } from '@/components/brand-mentions/mentionUtils'
import {
	BRAND_MENTION_PLATFORMS,
	PLATFORM_LABELS,
	type BrandMentionPlatform,
} from '@/types/brandMentions'

type PreviewMention = {
	platform: BrandMentionPlatform
	author: string
	text: string
	title?: string
	kind?: 'Comment'
	time: string
}

function previewMentions(isFrench: boolean): PreviewMention[] {
	if (isFrench) {
		return [
			{
				platform: 'x',
				author: 'Alex Martin',
				text: 'On a testé Feedbask aujourd’hui, le widget est vraiment fluide et les retours arrivent au bon endroit.',
				time: '2h',
			},
			{
				platform: 'reddit',
				author: 'u/saasfounders',
				title: 'Quelle stack pour collecter le feedback in-app ?',
				text: 'On cherche quelque chose de simple à installer. Feedbask a l’air propre et les mentions de marque aident à suivre les discussions.',
				kind: 'Comment',
				time: '5h',
			},
			{
				platform: 'facebook',
				author: 'Growth Lab',
				text: 'Quelqu’un a déjà utilisé le widget Feedbask ? Les premiers retours ont l’air très positifs.',
				time: '2j',
			},
			{
				platform: 'linkedin',
				author: 'Camille Durand',
				text: 'On entend de plus en plus parler de Feedbask. L’onboarding est clair et l’équipe produit répond vite.',
				time: '1j',
			},
			{
				platform: 'youtube',
				author: 'Indie Build',
				title: 'Comment on suit ce que les gens disent du produit',
				text: 'Feedbask centralise X, Reddit et le reste. Pratique pour répondre sans tout surveiller à la main.',
				kind: 'Comment',
				time: '3j',
			},
		]
	}

	return [
		{
			platform: 'x',
			author: 'Alex Martin',
			text: 'Tried Feedbask today. The widget feels smooth and the responses actually land in one place.',
			time: '2h',
		},
		{
			platform: 'reddit',
			author: 'u/saasfounders',
			title: 'What are you using for in-app feedback?',
			text: 'Looking for something easy to install. Feedbask looks clean and brand mentions help catch conversations we were missing.',
			kind: 'Comment',
			time: '5h',
		},
		{
			platform: 'facebook',
			author: 'Growth Lab',
			text: 'Has anyone used the Feedbask review widget yet? Early comments look really positive.',
			time: '2d',
		},
		{
			platform: 'linkedin',
			author: 'Camille Durand',
			text: 'Hearing more teams talk about Feedbask. Onboarding is clear and the product team replies fast.',
			time: '1d',
		},
		{
			platform: 'youtube',
			author: 'Indie Build',
			title: 'How we keep up with what people say about the product',
			text: 'Feedbask pulls X, Reddit and the rest into one inbox. Much easier than checking every network by hand.',
			kind: 'Comment',
			time: '3d',
		},
	]
}

export function BrandMentionsLandingPreview() {
	const { t, i18n } = useTranslation()
	const isFrench = Boolean(i18n.language?.startsWith('fr'))
	const mentions = useMemo(() => previewMentions(isFrench), [isFrench])
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const rootRef = useRef<HTMLDivElement>(null)
	const [inView, setInView] = useState(false)
	const mobile = useMediaQuery('(max-width: 767px)')
	const [selectedIndex, setSelectedIndex] = useState(0)

	useEffect(() => {
		const element = rootRef.current
		if (!element) return
		const observer = new IntersectionObserver(
			([entry]) => {
				setInView(entry.isIntersecting)
			},
			{ threshold: 0.35 }
		)
		observer.observe(element)
		return () => observer.disconnect()
	}, [])

 useEffect(() => {
  if (!inView || reducedMotion || mobile) return
  const timer = setInterval(() => setSelectedIndex(index => (index + 1) % mentions.length), 3500)
  return () => clearInterval(timer)
 }, [inView, reducedMotion, mobile, mentions.length])
 const visibleMentions = mobile ? mentions.slice(0, 2) : mentions
 const selected = mentions[selectedIndex]

	return (
		<div
			ref={rootRef}
			aria-hidden="true"
			className="fb-mentions-preview relative h-[30rem] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl sm:h-[32rem]"
		>
			<div className="pointer-events-none flex h-full select-none">
				<aside className="flex h-full w-full flex-col border-r border-border/60 bg-background sm:w-[17.5rem] sm:min-w-[17.5rem] sm:max-w-[17.5rem]">
					<div className="border-b border-border/60 bg-card/30 px-4 py-4">
						<div className="mb-3 flex items-center gap-2">
							<AtSign className="h-5 w-5 shrink-0 text-primary" />
							<p className="truncate text-lg font-semibold tracking-tight">
								{t('brandMentions.title')}
							</p>
						</div>
						<div className="flex flex-wrap gap-2">
							{BRAND_MENTION_PLATFORMS.map((platform) => (
								<span
									key={platform}
									title={PLATFORM_LABELS[platform]}
									className="inline-flex items-center justify-center rounded-md bg-muted p-1.5 text-muted-foreground"
								>
									<PlatformIcon platform={platform} className="h-3.5 w-3.5" colored />
								</span>
							))}
						</div>
					</div>
					<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
							<div className="space-y-2 px-2 py-2">
								{visibleMentions.map((mention, index) => {
									const isSelected = selectedIndex === index
									return (
										<div
											key={`${mention.platform}-${index}`}
											className={cn(
												'w-full rounded-xl border p-4 text-left',
												'bg-background border-border',
												'animate-in fade-in slide-in-from-top-2 duration-300',
												isSelected &&
													'bg-primary/10 border-primary/50 shadow-sm ring-1 ring-primary/30'
											)}
										>
											<div className="flex items-start gap-3">
												<div
													className={cn(
														'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted ring-2 ring-transparent',
														isSelected && 'ring-primary/30'
													)}
												>
													<PlatformIcon
														platform={mention.platform}
														className="h-5 w-5"
														colored
													/>
												</div>
												<div className="min-w-0 flex-1">
													<div className="mb-1 flex items-center justify-between gap-2">
														<span className="min-w-0 truncate text-sm font-medium text-foreground">
															{mention.author}
														</span>
														<div className="flex shrink-0 items-center gap-1.5">
															{mention.kind === 'Comment' && (
																<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
																	{t('brandMentions.commentKind')}
																</span>
															)}
															<span className="text-xs font-medium text-muted-foreground">
																{mention.time}
															</span>
														</div>
													</div>
													<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
														{mention.title || mention.text}
													</p>
												</div>
											</div>
										</div>
									)
								})}
							</div>
					</div>
				</aside>
				<main className="hidden min-w-0 flex-1 flex-col overflow-hidden bg-muted/20 sm:flex">
					{selected ? (
						<div
							key={selected.platform}
							className="flex h-full flex-col overflow-hidden bg-background animate-in fade-in duration-300"
						>
							<header className="flex-shrink-0 border-b bg-background/95 px-3 py-3">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
										<PlatformIcon
											platform={selected.platform}
											className="h-5 w-5"
											colored
										/>
									</div>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<p className="truncate text-base font-semibold">
												{selected.author}
											</p>
											{selected.kind === 'Comment' && (
												<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
													{t('brandMentions.commentKind')}
												</span>
											)}
										</div>
										<div className="flex items-center gap-2 text-xs text-muted-foreground">
											<span>{PLATFORM_LABELS[selected.platform]}</span>
											<span>•</span>
											<span>{selected.time}</span>
										</div>
									</div>
								</div>
							</header>
							<div className="flex-1 overflow-hidden p-4">
								<article className="mx-auto max-w-3xl">
									{selected.title && (
										<h3 className="mb-3 text-lg font-semibold">
											{highlightBrand(selected.title, 'Feedbask')}
										</h3>
									)}
									<p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
										{highlightBrand(selected.text, 'Feedbask')}
									</p>
								</article>
							</div>
							<footer className="flex-shrink-0 border-t bg-background/95 p-3">
								<div className="flex items-center gap-3 rounded-xl border-2 border-border/40 bg-muted/30 px-4 py-3">
									<span className="min-w-0 flex-1 text-sm text-muted-foreground">
										{t('brandMentions.replyPlaceholder', {
											platform: PLATFORM_LABELS[selected.platform],
										})}
									</span>
									<Button size="icon" className="h-9 w-9 shrink-0 shadow-md" tabIndex={-1}>
										<Send className="h-4 w-4" />
									</Button>
								</div>
							</footer>
						</div>
					) : (
						<div className="flex h-full flex-col items-center justify-center p-8 text-center">
							<AtSign className="mb-4 h-16 w-16 text-muted-foreground/50" />
							<p className="text-sm text-muted-foreground">
								{t('brandMentions.selectMention')}
							</p>
						</div>
					)}
				</main>
			</div>
		</div>
	)
}
